import React from 'react';
import { 
  Box, 
  Button, 
  Grid, 
  TextField,
  ButtonGroup,
  Tooltip,
  InputAdornment
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

/**
 * Component for date range filtering
 */
const DateFilters = ({ 
  startDate, 
  endDate, 
  activeFilter, 
  handleStartDateChange, 
  handleEndDateChange,
  setDateToday,
  setDateYesterday,
  setDateRange
}) => {
  // Custom TextField renderer for more compact date fields
  const renderCompactTextField = (params) => (
    <TextField 
      {...params} 
      fullWidth 
      size="small" 
      InputProps={{
        ...params.InputProps,
        sx: { 
          height: '40px',
          fontSize: '0.875rem'
        },
        startAdornment: (
          <InputAdornment position="start">
            <CalendarTodayIcon fontSize="small" color="action" />
          </InputAdornment>
        )
      }}
      sx={{ 
        '& .MuiInputLabel-root': {
          transform: 'translate(14px, 9px) scale(0.75)',
        },
        '& .MuiInputLabel-shrink': {
          transform: 'translate(14px, -6px) scale(0.75)',
        }
      }}
    />
  );

  return (
    <Grid container spacing={2} alignItems="center">
      {/* Date Range Pickers - Move to left */}
      <Grid item xs={12} sm={6} md={6}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={handleStartDateChange}
              renderInput={renderCompactTextField}
              maxDate={endDate || new Date()}
              disableFuture
            />
          </LocalizationProvider>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={handleEndDateChange}
              renderInput={renderCompactTextField}
              minDate={startDate}
              maxDate={new Date()}
              disableFuture
            />
          </LocalizationProvider>
        </Box>
      </Grid>
      
      {/* Quick Date Filters - Use ButtonGroup for more compact display */}
      <Grid item xs={12} sm={6} md={6}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ButtonGroup 
            size="small" 
            variant="outlined" 
            sx={{ 
              display: 'flex', 
              flexWrap: 'nowrap', 
              height: '40px',
              width: { xs: '100%', md: 'auto' }
            }}
          >
            <Tooltip title="Today">
              <Button 
                variant={activeFilter === "today" ? "contained" : "outlined"} 
                onClick={setDateToday}
                sx={{ 
                  fontSize: '0.75rem', 
                  py: 0.5,
                  px: 2, // Wider padding
                  height: '100%',
                  minWidth: { xs: '60px', md: '80px' }, // Set minimum width
                  bgcolor: activeFilter === "today" ? "primary.main" : "transparent"
                }}
              >
                Today
              </Button>
            </Tooltip>
            <Tooltip title="Yesterday">
              <Button 
                variant={activeFilter === "yesterday" ? "contained" : "outlined"} 
                onClick={setDateYesterday}
                sx={{ 
                  fontSize: '0.75rem', 
                  py: 0.5,
                  px: 2, // Wider padding
                  height: '100%',
                  minWidth: { xs: '70px', md: '90px' }, // Set minimum width
                  bgcolor: activeFilter === "yesterday" ? "primary.main" : "transparent"
                }}
              >
                Yesterday
              </Button>
            </Tooltip>
            <Tooltip title="Last 3 days">
              <Button 
                variant={activeFilter === "last3days" ? "contained" : "outlined"} 
                onClick={() => setDateRange(3)}
                sx={{ 
                  fontSize: '0.75rem', 
                  py: 0.5,
                  px: 1.5, // Wider padding
                  height: '100%',
                  minWidth: { xs: '50px', md: '60px' }, // Set minimum width
                  bgcolor: activeFilter === "last3days" ? "primary.main" : "transparent"
                }}
              >
                3d
              </Button>
            </Tooltip>
            <Tooltip title="Last 7 days">
              <Button 
                variant={activeFilter === "last7days" ? "contained" : "outlined"} 
                onClick={() => setDateRange(7)}
                sx={{ 
                  fontSize: '0.75rem', 
                  py: 0.5,
                  px: 1.5, // Wider padding
                  height: '100%',
                  minWidth: { xs: '50px', md: '60px' }, // Set minimum width
                  bgcolor: activeFilter === "last7days" ? "primary.main" : "transparent"
                }}
              >
                7d
              </Button>
            </Tooltip>
          </ButtonGroup>
        </Box>
      </Grid>
    </Grid>
  );
};

export default DateFilters; 